import { mutate } from "swr";
import { IDENTITY_KEY } from "../hooks/identityKey";

const SOMETHING_WENT_WRONG = "Something went wrong. Please try again.";

// /api/auth/login and /api/auth/signup answer errors as plain text, the newer endpoints as JSON.
const bodyOf = async (response) => {
  const text = await response.text();

  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text };
  }
};

const send = async (url, { method = "GET", body } = {}) => {
  try {
    const response = await fetch(url, {
      method,
      credentials: "same-origin",
      headers: body ? { "Content-Type": "application/json" } : {},
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await bodyOf(response);

    return { ok: response.ok, status: response.status, data };
  } catch {
    return { ok: false, status: 0, data: { message: SOMETHING_WENT_WRONG } };
  }
};

export const messageOf = (result) =>
  result.data?.message ?? result.data?.error ?? SOMETHING_WENT_WRONG;

export const refreshIdentity = () => mutate(IDENTITY_KEY);

const AUTH_ENDPOINT = Object.freeze({
  login: "/api/auth/login",
  create: "/api/auth/signup",
});

export const authenticate = ({ mode, email, password }) =>
  send(AUTH_ENDPOINT[mode], { method: "POST", body: { email, password } });

export const requestPasswordReset = (email) =>
  send("/api/auth/passwordReset", { method: "POST", body: { email } });

export const passwordLinkStatus = (t) =>
  send(`/api/auth/passwordLink?${new URLSearchParams({ t })}`);

export const changePasswordByLink = ({ t, password, passwordConfirm }) =>
  send("/api/auth/passwordLink", {
    method: "POST",
    body: { t, password, passwordConfirm },
  });

export const userInvite = (it) =>
  send(`/api/userInvite/accept?${new URLSearchParams({ it })}`);

export const acceptUserInvite = ({ token, password }) =>
  send("/api/userInvite/accept", { method: "POST", body: { token, password } });

export const talentInvite = (it) =>
  send(`/api/influencer/invite?${new URLSearchParams({ it })}`);

export const acceptTalentInvite = ({ token, password }) =>
  send("/api/influencer/inviteAccept", {
    method: "POST",
    body: { token, password },
  });

export const printBoxDestination = (printBoxId) =>
  send(`/api/printBox/${encodeURIComponent(printBoxId)}/destination`);

export const secondFactor = (proof) =>
  send("/api/auth/mfa", { method: "POST", body: proof });

export const secondFactorPasskeyOptions = () =>
  send("/api/auth/mfa/passkeyOptions", { method: "POST" });

export const passkeySignInOptions = () =>
  send("/api/auth/passkey/options", { method: "POST" });

export const passkeySignIn = ({ ceremonyId, response }) =>
  send("/api/auth/passkey", {
    method: "POST",
    body: { ceremonyId, response },
  });

export const stepUp = (proof) =>
  send("/api/auth/stepUp", { method: "POST", body: proof });

export const stepUpPasskeyOptions = () =>
  send("/api/auth/stepUp/passkeyOptions", { method: "POST" });

const SECURITY = "/api/account/security";

export const SECURITY_KEY = SECURITY;

export const startTotpSetup = () =>
  send(`${SECURITY}/totp`, { method: "POST" });

export const confirmTotpSetup = ({ ceremonyId, code, name }) =>
  send(`${SECURITY}/totp/confirm`, {
    method: "POST",
    body: { ceremonyId, code, ...(name ? { name } : {}) },
  });

export const passkeyRegistrationOptions = () =>
  send(`${SECURITY}/passkeys/options`, { method: "POST" });

export const savePasskey = ({ ceremonyId, response, name }) =>
  send(`${SECURITY}/passkeys`, {
    method: "POST",
    body: { ceremonyId, response, ...(name ? { name } : {}) },
  });

const FACTOR_PATH = Object.freeze({ passkey: "passkeys", totp: "totp" });

export const renameFactor = ({ kind, id, name }) =>
  send(`${SECURITY}/${FACTOR_PATH[kind]}/${id}`, {
    method: "PATCH",
    body: { name },
  });

export const removeFactor = ({ kind, id }) =>
  send(`${SECURITY}/${FACTOR_PATH[kind]}/${id}`, { method: "DELETE" });

export const removePassword = () =>
  send(`${SECURITY}/password`, { method: "DELETE" });

export const regenerateRecoveryCodes = () =>
  send(`${SECURITY}/recoveryCodes`, { method: "POST" });

export const changePassword = ({
  currentPassword,
  newPassword,
  confirmPassword,
}) =>
  send("/api/auth/changePasswordNew", {
    method: "PUT",
    body: { currentPassword, newPassword, confirmPassword },
  });

export const setPassword = (password) =>
  send("/api/auth/setPassword", { method: "POST", body: { password } });

export const resetUserMfa = (userId) =>
  send(`/api/admin/users/${encodeURIComponent(userId)}/mfa/reset`, {
    method: "POST",
  });

export const SESSIONS_KEY = "/api/account/sessions";

export const endSession = (sessionId) =>
  send(`${SESSIONS_KEY}/${encodeURIComponent(sessionId)}`, {
    method: "DELETE",
  });

export const signOutOtherSessions = () =>
  send(`${SESSIONS_KEY}/signOutOthers`, { method: "POST" });
